import React from "react";
import { Button } from "antd";
import { IModalState, INoteCardViewState, INotesCardView } from "./types/notes-card.types";
import dashboard from "../../../resources/dashboard";
import { useBaseComponent } from "../../../base/base-component";
import AddNoteModal from "../../my-notes/utils/add-note-modal";
import { Checkbox, Tooltip } from "antd/lib";
import { CalendarClock, NotebookPen, NotepadText, Plus } from "lucide-react";
import { Note, Priority } from "../../../domains/models/note";
import { useNavigate } from "react-router-dom";
import LoadingSkeleton from "../../common/loading-skeleton/loading-skeleton";
import EmptyData from "../../common/empty-data/empty-data";
import dayjs from "dayjs";

const NotesCardView: React.FC<INotesCardView> = (props) => {
    const resource = dashboard.notesCard;
    const {notes, upsertNotes} = props;
    const navigate = useNavigate();

    const initialModalState: IModalState = {
        mode: "add",
        open: false,
        noteData: null
    };

    const {state, updateState} = useBaseComponent<INotesCardView, INoteCardViewState>(props, {
        initialState: () => ({
            internal: {
                noteModalState: initialModalState,
            }
        })
    });

    const openAddNoteModal = () => {
        updateState('internal.noteModalState', {
            mode: "add",
            open: true,
            noteData: null
        });
    }

    const handleModalClose = () => {
        updateState('internal.noteModalState', {
            mode: "add",
            open: false,
            noteData: null
        });
    }

    const renderDate = (date?: string) => (date ? dayjs(date).format('DD MMM YYYY') : '');
    const renderCreatedDate = (date?: string) => (date ? dayjs(date).format('DD MMM ') : '');
    const isCompleted = (note: Note) => {
        return note.status === 'COMPLETED';
    }

    const handleOnClick = () => {
        navigate('/my-notes')
    }

    return (
        <div className="dashboard-notes-card dashboard-card" onClick={handleOnClick}>
            <div className={'dashboard-card__header'}>
                <span className={'dashboard-card__icon-wrapper dashboard-notes-card__icon-wrapper'}>
                       <NotebookPen className={'dashboard-card__icon dashboard-notes-card__icon'}/>
                </span>

                <h5 className={'dashboard-card__title'}>
                    {resource.title}
                    <p className={'dashboard-card__description'}>Capture ideas instantly.</p>
                </h5>
                <span className={'dashboard-notes-card__notes-count'}>
                    {notes.length} {notes.length === 1 ? resource.notes.single : resource.notes.multiple}
                </span>
                <Button
                    className="dashboard-notes-card__add-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        openAddNoteModal();
                    }}
                >
                    <Plus/>
                </Button>
            </div>

            <LoadingSkeleton loading={props.loading}>
                {notes.length > 0 ?
                    (<div className="dashboard-notes-card__list">
                            {notes.map((item) => (
                                <div
                                    className={`dashboard-notes-card__list-item
                                ${isCompleted(item) ? 'completed' : ''}
                                ${item.colorKey ? item.colorKey : 'default'}`}
                                    key={item.id}
                                >
                                    <div className={'dashboard-notes-card__list-item__header'}>
                                        <Tooltip
                                            title={!isCompleted(item) ? "mark note as complete" : "mark note as undone"}
                                            classNames={{
                                                root: "mark-note-complete-tooltip",
                                            }}

                                        >
                                            <Checkbox
                                                className={'dashboard-notes-card__list-item__mark-done-btn'}
                                                checked={isCompleted(item)}
                                                onChange={() => {
                                                    props.handleMarkStatus(item.id);
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                }}
                                                id={item.id}
                                            />
                                        </Tooltip>
                                        <h4 className={'title'}>
                                            {item.title}
                                        </h4>
                                        <span className={'created-date'}>
                                    {renderCreatedDate(item.auditInfo?.createdOn)}
                            </span>
                                    </div>
                                    <div
                                        className={'dashboard-notes-card__list-item__content'}
                                        dangerouslySetInnerHTML={{__html: item.content}}
                                    />

                                    <div className={'dashboard-notes-card__list-item__time-priority'}>
                                        {item.priority &&
                                        <span
                                            className={`priority ${item.priority}`}>{Priority[item.priority]}</span>
                                        }
                                        {item.dueDate && <span className={'due-date'}>
                                    <CalendarClock className={'due-date-icon'}/>
                                            {renderDate(item.dueDate)}</span>
                                        }
                                    </div>

                                    <div className={'dashboard-notes-card__footer'}>
                                        <div className={'tags-wrapper'}>
                                            {item.tag?.map((t, index) => (
                                                <span className={'tag'} key={index}>
                                               #{t}
                                        </span>
                                            ))}
                                        </div>
                                    </div>

                                </div>
                            ))}
                        </div>

                    ) : (
                        <EmptyData
                            icon={<NotepadText className={'dashboard-note-empty-icon'}/>}
                            title={" No notes yet"}
                            subtitle={"Tap the plus icon to create your first note!"}/>
                    )
                }
            </LoadingSkeleton>

            <div
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <AddNoteModal
                    modalState={state.internal.noteModalState}
                    closeAddNoteModal={handleModalClose}
                    upsertNotes={upsertNotes}
                />
            </div>
        </div>
    )
};

export default NotesCardView;
